/*
 * <bl-carousel> (BL)
 * ---------------------------------------------------------------------------------------------------------------------
 * Shared carousel engine for "Product groups (BL)" and "Recipe slider (BL)".
 *
 * Only the track ([data-carousel-track]) moves: swipe (native scroll), mouse drag, arrows, dots and autoplay. Content
 * lives in layers ([data-carousel-slide="index"], one or more per slide) that stay in place:
 *   transition="fade": the active slide's layers fade in once the track stops moving.
 *   transition="wipe": each layer is cropped to the part of the track its slide currently covers, frame by frame.
 *
 * Attributes:
 *   autoplay="6"       seconds between slides; stops for good once the visitor takes over
 *   autoplay-media     optional media query; autoplay only runs while it matches, e.g. "(max-width: 999px)"
 *   loop               infinite loop (copies of the first/last track slides are added at the ends)
 *   transition         "fade" (default) or "wipe"
 *   active-color-var   CSS variable read from the active layer and exposed on the host as --carousel-active-color
 *
 * Slides may be narrower than the track (padding + gap for a peeking/bleeding carousel); positions are measured from
 * the slides themselves, so the track must be their offsetParent (position: relative in bl-carousel.css).
 *
 * Markup hooks: [data-carousel-prev], [data-carousel-next] (any number of each), [data-carousel-dot], [data-block-id] on layers (theme
 * editor block selection). Active layers get .is-active; inactive layers are inert. The host gets .is-dragging.
 */
if (!window.customElements.get('bl-carousel')) {
  class BlCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');

      if (!this.track) {
        return;
      }

      this.slideCount = this.track.children.length;
      this.layers = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      // Any number of prev/next controls (e.g. arrows plus a "Swipe" button)
      this.prevButtons = Array.from(this.querySelectorAll('[data-carousel-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-carousel-next]'));
      this.index = 0;
      this.interrupted = false;
      this.loop = this.hasAttribute('loop') && this.slideCount > 1;
      this.wipe = this.getAttribute('transition') === 'wipe';
      this.activeColorVar = this.getAttribute('active-color-var');

      if (this.loop) {
        this.addLoopCopies();
      }

      this.offset = this.loop ? 1 : 0;

      this.prevButtons.forEach((button) => button.addEventListener('click', () => this.userGoTo(this.index - 1)));
      this.nextButtons.forEach((button) => button.addEventListener('click', () => this.userGoTo(this.index + 1)));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.userGoTo(index)));

      // Content only changes (fade) once the track has stopped moving; wipe follows it frame by frame
      this.onScroll = () => {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 150);

        if (this.wipe) {
          cancelAnimationFrame(this.wipeFrame);
          this.wipeFrame = requestAnimationFrame(() => this.renderWipe());
        }
      };
      this.onScrollEnd = () => this.settle();

      // Only sideways gestures count as the visitor taking over; scrolling the page up/down does not
      this.onInterrupt = () => this.interrupt();

      this.onWheel = (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.interrupt();
      };

      this.onTouchStart = (event) => {
        this.touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      };

      this.onTouchMove = (event) => {
        if (!this.touchStart || this.interrupted) return;

        const deltaX = Math.abs(event.touches[0].clientX - this.touchStart.x);
        const deltaY = Math.abs(event.touches[0].clientY - this.touchStart.y);
        if (deltaX > 10 && deltaX > deltaY) this.interrupt();
      };

      this.onPointerDown = (event) => this.startDrag(event);
      this.onDragStart = (event) => event.preventDefault();

      // Theme editor: jump to the block being edited
      this.onBlockSelect = (event) => {
        const layer = this.layers.find((element) => element.dataset.blockId === event.detail?.blockId);

        if (layer) {
          this.interrupt();
          this.goTo(parseInt(layer.dataset.carouselSlide, 10));
        }
      };

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('scrollend', this.onScrollEnd);
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.track.addEventListener('wheel', this.onWheel, { passive: true });
      this.addEventListener('keydown', this.onInterrupt);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('dragstart', this.onDragStart);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Keep the current slide in place on resize, and when a hidden layout becomes visible
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.dragging) this.jumpTo(this.index);
        this.renderWipe();
      });
      this.resizeObserver.observe(this.track);

      this.activate(0);
      this.setupAutoplay();
    }

    disconnectedCallback() {
      clearInterval(this.autoplayTimer);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.wipeFrame);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    // Copies of the last and first track slides sit before and after the real ones. Landing on a copy jumps instantly
    // to the matching real slide, which looks identical, so the visitor can keep going in one direction.
    addLoopCopies() {
      const slides = Array.from(this.track.children);

      const [firstCopy, lastCopy] = [slides[0], slides[slides.length - 1]].map((slide) => {
        const copy = slide.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');

        [copy, ...copy.querySelectorAll('*')].forEach((element) => {
          Array.from(element.attributes)
            .filter((attribute) => attribute.name.startsWith('data-shopify') || attribute.name === 'id')
            .forEach((attribute) => element.removeAttribute(attribute.name));
        });

        return copy;
      });

      this.track.prepend(lastCopy);
      this.track.append(firstCopy);
    }

    // Distance between two consecutive slides (slide width + gap). Equals the track width for full-width slides.
    step() {
      const [first, second] = this.track.children;
      return second ? second.offsetLeft - first.offsetLeft : this.track.clientWidth;
    }

    // Scroll position of a slide. With loop, -1 is the copy of the last slide and slideCount the copy of the first.
    position(index) {
      return (index + this.offset) * this.step();
    }

    nearestIndex() {
      if (!this.track.clientWidth) {
        return this.index;
      }

      return Math.round(this.track.scrollLeft / this.step()) - this.offset;
    }

    normalize(index) {
      return ((index % this.slideCount) + this.slideCount) % this.slideCount;
    }

    goTo(index) {
      const min = this.loop ? -1 : 0;
      const max = this.loop ? this.slideCount : this.slideCount - 1;
      const target = Math.max(min, Math.min(index, max));

      // Layout hidden at this breakpoint: nothing to scroll
      if (!this.track.clientWidth) {
        this.activate(this.normalize(target));
        return;
      }

      this.track.scrollTo({ left: this.position(target), behavior: 'smooth' });

      // No scroll happens when already in place, so settle straight away
      if (Math.abs(this.track.scrollLeft - this.position(target)) < 1) {
        this.settle();
      }
    }

    // Several slides visible at once: the track can reach its end before the last slide is at the start
    atEnd() {
      return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
    }

    jumpTo(index) {
      if (this.track.clientWidth) {
        this.track.scrollLeft = this.position(index);
      }
    }

    userGoTo(index) {
      this.interrupt();
      this.goTo(index);
    }

    settle() {
      if (this.dragging) {
        return;
      }

      this.track.style.removeProperty('scroll-snap-type');

      const index = this.nearestIndex();
      const realIndex = this.normalize(index);

      // Landed on a loop copy: swap to the real slide without animation
      if (index !== realIndex) {
        this.jumpTo(realIndex);
      }

      this.activate(realIndex);
    }

    activate(index) {
      this.index = index;

      this.layers.forEach((layer) => {
        const active = parseInt(layer.dataset.carouselSlide, 10) === index;
        layer.classList.toggle('is-active', active);
        layer.toggleAttribute('inert', !active);
      });

      this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'));

      const atStart = !this.loop && index === 0;
      const atLast = !this.loop && (index === this.slideCount - 1 || (this.track.clientWidth > 0 && this.atEnd()));
      this.prevButtons.forEach((button) => (button.disabled = atStart));
      this.nextButtons.forEach((button) => (button.disabled = atLast));

      if (this.activeColorVar) {
        const layer = this.layers.find((element) => element.classList.contains('is-active'));
        const color = layer ? getComputedStyle(layer).getPropertyValue(this.activeColorVar).trim() : '';
        if (color) this.style.setProperty('--carousel-active-color', color);
      }

      this.renderWipe();
    }

    // Crops each layer to the part of the track its slide currently covers
    renderWipe() {
      if (!this.wipe) {
        return;
      }

      if (!this.track.clientWidth) {
        return;
      }

      const width = this.step();

      const scroll = this.track.scrollLeft;
      const trackLeft = this.track.getBoundingClientRect().left;
      const firstPosition = Math.floor(scroll / width);
      const ranges = new Map();

      // At most two slides are on screen at once
      for (let position = firstPosition; position <= firstPosition + 1; position++) {
        const start = Math.max(0, position * width - scroll);
        const end = Math.min(width, (position + 1) * width - scroll);
        const index = this.loop ? this.normalize(position - this.offset) : position - this.offset;

        if (end - start >= 0.5 && index >= 0 && index < this.slideCount) {
          ranges.set(index, [start, end]);
        }
      }

      this.layers.forEach((layer) => {
        const range = ranges.get(parseInt(layer.dataset.carouselSlide, 10));

        if (!range) {
          layer.style.clipPath = 'inset(0 0 0 100%)';
          return;
        }

        const layerLeft = layer.getBoundingClientRect().left - trackLeft;
        const insetLeft = Math.max(0, range[0] - layerLeft);
        const insetRight = Math.max(0, layer.offsetWidth - (range[1] - layerLeft));
        layer.style.clipPath = `inset(0 ${insetRight}px 0 ${insetLeft}px)`;
      });
    }

    // Mouse drag; touch devices use native scrolling on the track
    startDrag(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('a, button, input, select, textarea')) {
        return;
      }

      event.preventDefault();
      this.dragging = true;

      const startX = event.clientX;
      const startScroll = this.track.scrollLeft;
      const startIndex = this.index;
      let deltaX = 0;

      this.track.style.scrollSnapType = 'none';
      this.classList.add('is-dragging');

      const onMove = (moveEvent) => {
        deltaX = moveEvent.clientX - startX;
        this.track.scrollLeft = startScroll - deltaX;

        if (Math.abs(deltaX) > 5) this.interrupt();
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);

        this.dragging = false;
        this.classList.remove('is-dragging');

        const threshold = Math.min(80, this.track.clientWidth * 0.1);
        let target = startIndex;
        if (deltaX < -threshold) target = startIndex + 1;
        if (deltaX > threshold) target = startIndex - 1;

        this.goTo(target);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    setupAutoplay() {
      const seconds = parseInt(this.getAttribute('autoplay'), 10);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!seconds || reducedMotion || this.slideCount < 2) {
        return;
      }

      this.visible = false;
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
      });
      this.observer.observe(this);

      const media = this.getAttribute('autoplay-media');
      this.autoplayQuery = media ? window.matchMedia(media) : null;

      this.autoplayTimer = setInterval(() => {
        // Skip ticks while off-screen, in a hidden tab, while this layout is hidden (other breakpoint), or outside the
        // screen sizes autoplay is meant for
        if (this.interrupted || !this.visible || document.hidden || this.offsetParent === null) {
          return;
        }

        if (this.autoplayQuery && !this.autoplayQuery.matches) {
          return;
        }

        // With loop the carousel keeps moving forward; without it, it rewinds to the first slide at the end
        if (this.loop) {
          this.goTo(this.index + 1);
        } else if (this.atEnd() || this.index + 1 >= this.slideCount) {
          this.goTo(0);
        } else {
          this.goTo(this.index + 1);
        }
      }, seconds * 1000);
    }

    // Stops autoplay until the page is reloaded
    interrupt() {
      this.interrupted = true;
      clearInterval(this.autoplayTimer);
    }
  }

  window.customElements.define('bl-carousel', BlCarousel);
}
